import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toaster } from 'baseui/toast';
import {
  getPropertyAdminAuthorizedEstates,
  getPropertyAdminPendingRequests,
  postPropertyAdminAccessRequest,
  putPropertyAdminAuthorizedEstate,
  deletePropertyAdminAuthorizedEstate,
  type GetPropertyAdminAuthorizedEstatesParams,
  type GetPropertyAdminPendingRequestsParams,
  type NewPropertyAdminAccessRequest,
  type PropertyAdminAuthorizedEstate,
  type PropertyAdminPendingRequest,
} from '../api/propertyAdmin';

// Hook for getting authorized estates — only runs when user_id is provided
export const usePropertyAdminAuthorizedEstates = (params?: GetPropertyAdminAuthorizedEstatesParams) => {
  const queryParams = {
    page: params?.page || 1,
    limit: params?.limit || 100,
    expand: 'estate,estate_id',
    order_by: 'created_at',
    order_direction: 'DESC' as const,
    ...params,
  };

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['/property_admin_authorized_estates', queryParams],
    queryFn: ({ signal }) => getPropertyAdminAuthorizedEstates(queryParams, signal),
    enabled: !!params?.user_id,
    retry: 1,
    staleTime: 1000 * 60 * 5,
  });

  const authorizedEstates: PropertyAdminAuthorizedEstate[] = Array.isArray(response?.data) ? response.data : [];
  const totalPages = (response?.data as any)?.total_pages || 1;
  const currentPage = (response?.data as any)?.page || (params?.page || 1);
  const totalElements = (response?.data as any)?.total_elements || 0;

  return {
    authorizedEstates,
    isLoading,
    error,
    totalPages,
    currentPage,
    totalElements,
  };
};

// Hook for getting pending requests
export const usePropertyAdminPendingRequests = (params?: GetPropertyAdminPendingRequestsParams) => {
  const queryParams = {
    page: params?.page || 1,
    limit: params?.limit || 100,
    expand: 'estate,estate_id',
    order_by: 'created_at',
    order_direction: 'DESC' as const,
    ...params,
  };

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['/property_admin_pending_requests', queryParams],
    queryFn: ({ signal }) => getPropertyAdminPendingRequests(queryParams, signal),
    enabled: !!params?.user_id,
    retry: 1,
    staleTime: 1000 * 60 * 5,
  });

  const pendingRequests: PropertyAdminPendingRequest[] = Array.isArray(response?.data) ? response.data : [];
  const totalPages = (response?.data as any)?.total_pages || 1;
  const currentPage = (response?.data as any)?.page || (params?.page || 1);
  const totalElements = (response?.data as any)?.total_elements || 0;

  return {
    pendingRequests,
    isLoading,
    error,
    totalPages,
    currentPage,
    totalElements,
  };
};

// Hook for submitting new access request
export const useSubmitPropertyAdminAccessRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: NewPropertyAdminAccessRequest) => postPropertyAdminAccessRequest(data),
    onSuccess: (response) => {
      // Invalidate and refetch pending requests
      queryClient.invalidateQueries({ 
        queryKey: ['/property_admin_pending_requests'],
        exact: false 
      });
      
      toaster.positive('Access request submitted successfully', {
        autoHideDuration: 3000,
      });
      
      return response;
    },
    onError: (error: any) => {
      console.error('Error submitting access request:', error);
      toaster.negative('Error submitting access request', {
        autoHideDuration: 3000,
      });
      throw error;
    },
  });
};

// Hook for updating authorized estate (extend access)
export const useUpdatePropertyAdminAuthorizedEstate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<PropertyAdminAuthorizedEstate> }) =>
      putPropertyAdminAuthorizedEstate(id, data),
    onSuccess: (response, variables) => {
      // Invalidate and refetch authorized estates
      queryClient.invalidateQueries({ 
        queryKey: ['/property_admin_authorized_estates'],
        exact: false 
      });
      
      toaster.positive('Estate access updated successfully', {
        autoHideDuration: 3000,
      });
      
      return response;
    },
    onError: (error: any) => {
      console.error('Error updating estate access:', error);
      toaster.negative('Error updating estate access', {
        autoHideDuration: 3000,
      });
      throw error;
    },
  });
};

// Hook for removing authorized estate access
export const useRemovePropertyAdminAuthorizedEstate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deletePropertyAdminAuthorizedEstate(id),
    onSuccess: (response, id) => {
      // Invalidate and refetch authorized estates
      queryClient.invalidateQueries({ 
        queryKey: ['/property_admin_authorized_estates'],
        exact: false 
      });
      
      toaster.positive('Estate access removed successfully', {
        autoHideDuration: 3000,
      });
      
      return response;
    },
    onError: (error: any) => {
      console.error('Error removing estate access:', error);
      toaster.negative('Error removing estate access', {
        autoHideDuration: 3000,
      });
      throw error;
    },
  });
}; 